import {
    AdminApi,
    EditDraftPostContentDto,
    CreatePipelineJobsDto,
    TriggerEmailNotificationDto,
    PipelineStepName,
    RunMode,
    PipelineJob,
    NotificationResultDto,
} from '../../api/admin'
import {
    ListUsersQueryDto,
    ListDraftPostsQueryDto,
    BulkUpdateDraftPostStatusDto,
    AdminDraftPostDetailedDto,
    DraftPostFeedbackResponseDto,
    UpdateDraftPostStatusResponseDto,
    BulkUpdateDraftPostStatusResponseDto,
    OrgDto,
    CreateOrgDto,
    AdminCreateUserDto,
    AdminGetUserResponseDto,
    AdminUpdateUserDto,
    AdminTriggerLinkedInScrapeDto,
} from '../../types/admin-types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

// Assuming UserEntity = AdminGetUserResponseDto as defined in api/admin.ts
type UserEntity = AdminGetUserResponseDto

// Re-export specific types needed for component props or params
export type {
    EditDraftPostContentDto,
    CreatePipelineJobsDto,
    TriggerEmailNotificationDto,
    CreateOrgDto,
    AdminCreateUserDto,
    AdminUpdateUserDto,
    AdminTriggerLinkedInScrapeDto,
}
export { PipelineStepName, RunMode }

// Define a more specific error type for API errors with messages
interface ApiError extends Error {
    responseStatus?: number // Optional: include status code if needed
}

// Query key factory (align with useAdminQueries.ts)
const adminQueryKeys = {
    all: ['admin'] as const,
    users: (query?: ListUsersQueryDto) => [...adminQueryKeys.all, 'users', query] as const,
    user: (id: string | null) => [...adminQueryKeys.all, 'user', id] as const,
    draftPosts: (query?: ListDraftPostsQueryDto) =>
        [...adminQueryKeys.all, 'draftPosts', query] as const,
    draftPost: (id: string | null) => [...adminQueryKeys.all, 'draftPost', id] as const,
    orgs: () => [...adminQueryKeys.all, 'orgs'] as const,
}

// Hook to update draft post status
export const useUpdateDraftPostStatus = () => {
    const queryClient = useQueryClient()

    return useMutation<UpdateDraftPostStatusResponseDto, Error, { id: string; status: string }>({
        mutationFn: ({ id, status }) => AdminApi.updateStatus(id, status).then((res) => res.data),
        onSuccess: (_, variables) => {
            // Invalidate the specific post query
            queryClient.invalidateQueries({ queryKey: adminQueryKeys.draftPost(variables.id) })
            // Invalidate the list query
            queryClient.invalidateQueries({ queryKey: adminQueryKeys.draftPosts() })
        },
    })
}

// Hook to add feedback to a draft post
export const useAddDraftPostFeedback = () => {
    const queryClient = useQueryClient()

    return useMutation<DraftPostFeedbackResponseDto, Error, { id: string; content: string }>({
        mutationFn: ({ id, content }) => AdminApi.addFeedback(id, content).then((res) => res.data),
        onSuccess: (_, variables) => {
            // Invalidate the specific post query
            queryClient.invalidateQueries({ queryKey: adminQueryKeys.draftPost(variables.id) })
        },
    })
}

// Hook to edit draft post content
export const useEditDraftPostContent = () => {
    const queryClient = useQueryClient()

    return useMutation<
        AdminDraftPostDetailedDto,
        Error,
        { id: string; data: EditDraftPostContentDto }
    >({
        mutationFn: ({ id, data }) => AdminApi.editContent(id, data).then((res) => res.data),
        onSuccess: (data, variables) => {
            // Invalidate the specific post query to force a refetch of the full data
            queryClient.invalidateQueries({ queryKey: adminQueryKeys.draftPost(variables.id) })
            // Optionally invalidate the list query
            queryClient.invalidateQueries({ queryKey: adminQueryKeys.draftPosts() })
        },
    })
}

// Hook for bulk updating draft post status
export const useBulkUpdateDraftPostStatus = () => {
    const queryClient = useQueryClient()

    return useMutation<BulkUpdateDraftPostStatusResponseDto, Error, BulkUpdateDraftPostStatusDto>({
        mutationFn: (data) => AdminApi.bulkUpdateStatus(data).then((res) => res.data),
        onSuccess: (data, variables) => {
            // Invalidate all draft post lists
            queryClient.invalidateQueries({ queryKey: adminQueryKeys.draftPosts() })
            // Potentially invalidate individual posts
            variables.ids.forEach((id) => {
                queryClient.invalidateQueries({ queryKey: adminQueryKeys.draftPost(id) })
            })
        },
    })
}

// Hook to create pipeline jobs
export const useCreatePipelineJobs = () => {
    // No query invalidation needed typically, unless there's a UI showing job status
    return useMutation<{ jobs: PipelineJob[] }, Error, CreatePipelineJobsDto>({
        mutationFn: (config) => AdminApi.createPipelineJobs(config).then((res) => res.data),
        // Add onSuccess side effects if needed (e.g., toast notification)
    })
}

// Hook to trigger email notifications
export const useTriggerEmailNotification = () => {
    // No query invalidation needed typically
    return useMutation<NotificationResultDto, Error, TriggerEmailNotificationDto>({
        mutationFn: (config) => AdminApi.triggerEmailNotification(config).then((res) => res.data),
        // Add onSuccess side effects if needed (e.g., toast notification)
    })
}

// --- NEW Mutation Hooks ---

// Hook to create a new organization
export const useCreateOrg = () => {
    const queryClient = useQueryClient()
    return useMutation<OrgDto, Error, CreateOrgDto>({
        mutationFn: (data) => AdminApi.createOrg(data).then((res) => res.data),
        onSuccess: () => {
            // Invalidate the orgs list query to refetch
            queryClient.invalidateQueries({ queryKey: adminQueryKeys.orgs() })
        },
        // Consider adding onError for feedback
    })
}

// Hook to create a new user
export const useCreateUser = () => {
    const queryClient = useQueryClient()
    return useMutation<UserEntity, ApiError, AdminCreateUserDto>({
        mutationFn: async (data) => {
            try {
                const res = await AdminApi.createUser(data)
                return res.data
            } catch (error) {
                // Process the error here
                let errorMessage = 'An unknown error occurred.'
                let status: number | undefined

                if (error instanceof AxiosError && error.response?.data?.message) {
                    errorMessage = error.response.data.message
                    status = error.response.status
                } else if (error instanceof Error) {
                    errorMessage = error.message
                }

                // Throw a new error conforming to ApiError
                const apiError = new Error(errorMessage) as ApiError
                apiError.name = 'ApiError'
                apiError.responseStatus = status
                throw apiError
            }
        },
        onSuccess: () => {
            // Invalidate the users list query to refetch
            queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() })
        },
        // Consider adding onError for feedback
    })
}

// Hook to update an existing user
export const useUpdateUser = () => {
    const queryClient = useQueryClient()
    return useMutation<UserEntity, Error, { id: string; data: AdminUpdateUserDto }>({
        mutationFn: ({ id, data }) => AdminApi.updateUser(id, data).then((res) => res.data),
        onSuccess: (data, variables) => {
            // Option 1: Invalidate the specific user query and the list
            queryClient.invalidateQueries({ queryKey: adminQueryKeys.user(variables.id) })
            queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() })

            // Option 2: Update the user query cache directly for faster UI update
            // queryClient.setQueryData(adminQueryKeys.user(variables.id), data);
            // // Still invalidate list if list view might change (e.g., name sort)
            // queryClient.invalidateQueries({ queryKey: adminQueryKeys.users() });
        },
        // Consider adding onError for feedback
    })
}

// Hook to trigger LinkedIn scrape
export const useTriggerLinkedInScrape = () => {
    // No query invalidation needed by default unless we track scrape status
    return useMutation<
        { message: string },
        Error,
        { id: string; data: AdminTriggerLinkedInScrapeDto }
    >({
        mutationFn: ({ id, data }) =>
            AdminApi.triggerLinkedInScrape(id, data).then((res) => res.data),
        // Add onSuccess/onError for user feedback (e.g., toasts)
    })
}
